import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id?: string;
  email: string;
  name: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  role: string;
  language?: string;
  /** Persoenliche Zeitzone aus dem Profil — nur noch Rueckfallebene, siehe `tenantTimezone`. */
  timezone?: string;
  /**
   * Zeitzone der FIRMA (`tenant_settings.general.timezone`). Massgeblich fuer alle Datums-
   * und Zeitangaben: Listen, Filter und Berichte grenzen den Tag danach ab, damit jeder
   * Benutzer dieselbe Liste sieht.
   */
  tenantTimezone?: string;
  isVerified?: boolean;
  googleId?: string | null;
  hasUserPassword?: boolean;
}

interface Tenant {
  id: string;
  name: string;
}

// Permission types
type PermissionModule =
  | 'articles'
  | 'orders'
  | 'inventory'
  | 'shipping'
  | 'picking'
  | 'customers'
  | 'invoices'
  | 'accounting'
  | 'reports'
  | 'settings'
  | 'users'
  | 'integrations'
  | 'dashboard_figures';

type PermissionOperation = 'view' | 'create' | 'edit' | 'delete' | 'export';

type Permissions = Record<PermissionModule, Record<PermissionOperation, boolean>>;

interface Warehouse {
  id: string;
  name: string;
}

/**
 * Preisfokus (TICK-2026-01108): Kundengruppe, deren VK/Marge dieser Benutzer sich selbst in
 * Artikel-Detail, Artikelliste und Statistiken anzeigen laesst. `groupKey: null` = Standard
 * (Mandant hat keine oder keine aktive Kundengruppe als Fokus) — dann verhaelt sich alles
 * unveraendert wie ohne dieses Feature.
 */
interface PriceFocus {
  groupKey: string | null;
  groupName: string | null;
  /** V-0618: true = nur der Rückfall auf die Standardgruppe (keine eigene Wahl) → Auswahl zeigt „Standard“. */
  ist_standard?: boolean;
}

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  permissions: Permissions | null;
  /**
   * Dashboard-Kacheln, die für die Rolle dieses Benutzers abgeschaltet sind.
   *
   * Getrennt von `permissions`, weil es keine Berechtigung ist, sondern eine Anzeige-Vorgabe:
   * Ein Lagermitarbeiter darf den Umsatz vielleicht sehen — er soll ihn nur nicht auf seinem
   * Dashboard haben.
   */
  blockedWidgets: string[];
  tokenExpiresAt: number | null; // Unix timestamp in ms
  lastActivity: number | null; // Unix timestamp in ms
  activeWarehouse: Warehouse | null;
  priceFocus: PriceFocus | null;
  setUser: (user: User | null) => void;
  setTenant: (tenant: Tenant | null) => void;
  setPermissions: (permissions: Permissions | null) => void;
  setBlockedWidgets: (widgets: string[]) => void;
  setTokenExpiration: (expiresAt: number | null) => void;
  updateLastActivity: () => void;
  setActiveWarehouse: (warehouse: Warehouse | null) => void;
  setPriceFocus: (priceFocus: PriceFocus | null) => void;
  hasPermission: (module: PermissionModule, operation: PermissionOperation) => boolean;
  isAdmin: () => boolean;
  isEmailVerified: () => boolean;
  setEmailVerified: (verified: boolean) => void;
  clear: () => void;
}

// Parse JWT to get expiration time
export function parseJwtExpiration(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    if (payload.exp) {
      return payload.exp * 1000; // Convert to ms
    }
    return null;
  } catch {
    return null;
  }
}

// Parse JWT to get verification status
export function parseJwtVerificationStatus(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    const payload = JSON.parse(atob(parts[1]));
    return payload.is_verified === true;
  } catch {
    return false;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tenant: null,
      permissions: null,
      blockedWidgets: [],
      tokenExpiresAt: null,
      lastActivity: null,
      activeWarehouse: null,
      priceFocus: null,
      setUser: (user) => set({ user }),
      setTenant: (tenant) => set({ tenant }),
      setPermissions: (permissions) => set({ permissions }),
      setBlockedWidgets: (blockedWidgets) => set({ blockedWidgets }),
      setTokenExpiration: (expiresAt) => set({ tokenExpiresAt: expiresAt }),
      updateLastActivity: () => set({ lastActivity: Date.now() }),
      setActiveWarehouse: (warehouse) => set({ activeWarehouse: warehouse }),
      setPriceFocus: (priceFocus) => set({ priceFocus }),
      hasPermission: (module, operation) => {
        const state = get();
        if (!state.user) return false;
        if (state.user.role === 'admin') return true;
        if (!state.permissions) return false;
        const modulePerms = state.permissions[module];
        if (!modulePerms) return false;
        return modulePerms[operation] === true;
      },
      isAdmin: () => {
        const state = get();
        return state.user?.role === 'admin';
      },
      isEmailVerified: () => {
        const state = get();
        return state.user?.isVerified === true;
      },
      setEmailVerified: (verified: boolean) => {
        const state = get();
        if (state.user) {
          set({ user: { ...state.user, isVerified: verified } });
        }
      },
      clear: () =>
        set({
          user: null,
          tenant: null,
          permissions: null,
          tokenExpiresAt: null,
          lastActivity: null,
          activeWarehouse: null,
          priceFocus: null,
        }),
    }),
    {
      name: 'auth-storage',
    }
  )
);
